"use client";

import posthog from "posthog-js";
import { analyticsHostForAttraction, attractionById } from "@/lib/attractions/registry";
import { ANALYTICS_SCHEMA_VERSION, type AnalyticsEventName } from "./events";
import { sanitizePostHogCaptureResult, sanitizePostHogProperties } from "./posthog-sanitize";
import { customerSiteAnalyticsContext, type CustomerSiteAnalyticsContext } from "./customer-site";
import {
  getAttribution,
  getOrCreateAnonymousId,
  getOrCreateFirstLandingPath,
  getOrCreateSessionId,
  updateAttributionFromLocation,
  type Attribution,
} from "./session";

type TrackOpts = {
  channel?: string | null;
  skipFirstParty?: boolean;
};

function prefixProps(prefix: string, values: Record<string, string> | undefined): Record<string, string> {
  if (!values) return {};
  return Object.fromEntries(Object.entries(values).map(([k, v]) => [`${prefix}_${k}`, v]));
}

type TrackableProp = string | number | boolean | null | undefined;

export type AnalyticsUserProfile = {
  userId: string;
  email?: string | null;
  watches?: {
    activeCount?: number;
    monitoringCount?: number;
  };
};

function cleanEnvValue(value: string | undefined): string | undefined {
  const cleaned = value?.trim().replace(/^["']|["']$/g, "").trim();
  return cleaned || undefined;
}

function cleanPostHogHost(value: string | undefined): string | undefined {
  const cleaned = cleanEnvValue(value)?.replace(/\/+$/, "");
  return cleaned || undefined;
}

export function shouldTrackPostHogHost(rawHost: string | null | undefined): boolean {
  return customerSiteAnalyticsContext(rawHost ?? "", "/") !== null;
}

function stringProp(value: TrackableProp): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function detectEnvironment(): "development" | "production" {
  if (typeof window === "undefined") return "production";
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1" || host === "::1" || host.endsWith(".local") || host.endsWith(".localhost")) {
    return "development";
  }
  return "production";
}

let postHogInitialized = false;

function postHogConfig(attractionId: CustomerSiteAnalyticsContext["attractionId"]): { token: string; host: string } | null {
  const token = cleanEnvValue(process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN);
  const host = cleanPostHogHost(`https://${analyticsHostForAttraction(attractionById(attractionId))}`);
  if (!token || !host) return null;
  return { token, host };
}

export function initializePostHog() {
  if (typeof window === "undefined") return null;
  const customerSite = customerSiteAnalyticsContext(window.location.hostname, window.location.pathname);
  if (!customerSite) return null;
  const config = postHogConfig(customerSite.attractionId);
  if (!config) return null;
  if (!postHogInitialized) {
    const anonymousId = getOrCreateAnonymousId();
    posthog.init(config.token, {
      api_host: config.host,
      ui_host: "https://eu.posthog.com",
      defaults: "2026-01-30",
      autocapture: true,
      capture_pageview: "history_change",
      capture_pageleave: "if_capture_pageview",
      disable_session_recording: false,
      persistence: "localStorage+cookie",
      person_profiles: "identified_only",
      bootstrap: {
        distinctID: anonymousId,
        isIdentifiedID: false,
      },
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false,
      },
      capture_performance: {
        web_vitals: true,
        network_timing: true,
      },
      session_recording: {
        maskAllInputs: true,
        maskInputOptions: {
          password: true,
          email: true,
          tel: true,
          text: false,
        },
        maskTextClass: "ph-mask",
        maskTextSelector:
          ".ph-mask,[data-ph-mask],[data-sensitive],[data-user-email],[data-phone],[data-token],.StripeElement,#card-element",
        blockClass: "ph-no-capture",
        blockSelector:
          ".ph-no-capture,[data-ph-no-capture],[data-secret],[data-token],input[type='password'],input[name*='token' i],input[name*='secret' i],textarea[name*='message' i],.StripeElement,#card-element,[data-stripe]",
      },
      before_send: (capture) => {
        if (!customerSiteAnalyticsContext(window.location.hostname, window.location.pathname)) return null;
        return sanitizePostHogCaptureResult(capture);
      },
    });
    // Stamp every event with the environment so dev/local traffic can be excluded
    // via PostHog's "internal & test accounts" filter (environment = development).
    posthog.register({
      environment: detectEnvironment(),
      event_source: "client",
      analytics_schema_version: ANALYTICS_SCHEMA_VERSION,
      attraction_id: customerSite.attractionId,
      attraction_brand: customerSite.attractionBrand,
      attraction_host: customerSite.attractionHost,
    });
    postHogInitialized = true;
  }
  return posthog;
}

function cleanPostHogProperties(props: Record<string, TrackableProp>): Record<string, string | number | boolean | null> {
  return sanitizePostHogProperties(props) as Record<string, string | number | boolean | null>;
}

export function analyticsUserProfileProperties(
  user: AnalyticsUserProfile,
  attribution: Attribution,
  anonymousId: string,
  firstLandingPath: string | undefined,
) {
  return {
    set: {
      app_user_id: user.userId,
      email_present: Boolean(user.email),
      active_watch_count: user.watches?.activeCount ?? 0,
      monitoring_watch_count: user.watches?.monitoringCount ?? 0,
      has_active_watch: (user.watches?.activeCount ?? 0) > 0,
    },
    setOnce: {
      first_landing_path: firstLandingPath,
      first_referrer: attribution.referrer,
      first_anonymous_id: anonymousId,
      ...prefixProps("first", attribution.first),
    },
  };
}

export function identifyAnalyticsUser(user: AnalyticsUserProfile | null | undefined): void {
  if (!user?.userId) return;
  const client = initializePostHog();
  if (!client) return;
  updateAttributionFromLocation();
  const anonymousId = getOrCreateAnonymousId();
  const { set, setOnce } = analyticsUserProfileProperties(
    user,
    getAttribution(),
    anonymousId,
    getOrCreateFirstLandingPath(),
  );
  client.identify(user.userId, cleanPostHogProperties(set), cleanPostHogProperties(setOnce));
  // Keep a queryable event-level identifier on subsequent authenticated events.
  // PostHog still owns identity merging through identify(); this is only a
  // convenient, non-PII property for debugging and server/client correlation.
  client.register({ user_id: user.userId, app_user_id: user.userId });
}

export function resetAnalyticsUser(): void {
  if (!postHogInitialized || typeof window === "undefined") return;
  posthog.resetGroups();
  posthog.reset();
}

export function clientAnalyticsProperties(
  props?: Record<string, TrackableProp>,
  opts?: TrackOpts,
): Record<string, TrackableProp> {
  // Ensure cookies exist and attribution is up-to-date before we capture the event.
  const sessionId = getOrCreateSessionId();
  const anonymousId = getOrCreateAnonymousId();
  updateAttributionFromLocation();
  const attribution = getAttribution();

  const path =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : undefined;
  const customerSite =
    typeof window !== "undefined"
      ? customerSiteAnalyticsContext(window.location.hostname, window.location.pathname)
      : null;

  const baseProps = {
    session_id: sessionId,
    anonymous_id: anonymousId,
    path,
    attraction_id: customerSite?.attractionId,
    attraction_brand: customerSite?.attractionBrand,
    attraction_host: customerSite?.attractionHost,
    channel: opts?.channel ?? undefined,
    referrer: attribution.referrer,
    ...prefixProps("first", attribution.first),
    ...prefixProps("last", attribution.last),
    // Keep current/last-touch UTM keys at the top level for analytics exploration.
    ...(attribution.last ?? {}),
  };
  return { ...baseProps, ...(props ?? {}) };
}

export function trackEvent(
  name: AnalyticsEventName,
  props?: Record<string, TrackableProp>,
  opts?: TrackOpts,
): void {
  if (
    typeof window === "undefined" ||
    !customerSiteAnalyticsContext(window.location.hostname, window.location.pathname)
  ) {
    return;
  }
  if (opts?.skipFirstParty) return;
  const eventProps = clientAnalyticsProperties(props, opts);
  const client = initializePostHog();
  if (!client) return;

  client.capture(
    name,
    cleanPostHogProperties({
      ...eventProps,
      $current_url: window.location.href,
      $host: window.location.host,
      $pathname: window.location.pathname,
      $referrer: stringProp(eventProps.referrer) ?? document.referrer,
    }),
  );
}
